import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageLayout,
  Typography,
  SearchInput,
  ButtonsWrapper,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
  Drawer,
  DeleteModal,
  Loader,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
} from '@thinknet-gmbh/platform-common-ui';
import {
  ProjectStatusesEnum,
  PLATFORM_PERMISSIONS,
  PERMISSION_ACTIONS,
} from '@thinknet-gmbh/platform-common';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { useGetProjects } from 'src/hooks/projects/get-projects.hook';
import { useChangeProjectStatus } from 'src/hooks/projects/change-project-status.hook';
import { IProject } from 'src/pages/projects/projects.interfaces';
import PermissionCheck from 'src/components/permission-check';
import { useGetKWPConnectionByUserId } from 'src/hooks/interfaces/kwp/get-kwp-connection.hook';
import { useCheckIfTokenIsValid } from 'src/hooks/interfaces/kwp/check-token-validation.hook';
import { HeaderContainer } from './projects.styles';
import { ProjectsTable } from './components/projects-table';
import { PROJECT_MODALS } from './projects.constants';
import { ProjectTableFilter } from './components/project-table-filter';
import { PROJECTS_SUB_PAGES } from './sub-pages/create-project/create-project.constants';
import { KwpFetchingDataModal } from '../settings/components/interfaces/kwp/components/modals/kwp-fetching-data-modal-component';

const Project: React.FC = () => {
  const { t } = useTranslation(['projects', 'common']);
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [projectStatuses, setProjectStatuses] = useState<ProjectStatusesEnum[]>([]);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);

  const { projects, loading, paginationConfig, onTableChange } = useGetProjects({
    searchParam: searchValue,
    projectStatuses,
  });

  const { interfaceConnection } = useGetKWPConnectionByUserId();
  const { isTokenValid } = useCheckIfTokenIsValid(interfaceConnection?.link);

  const changeProjectStatus = useChangeProjectStatus({ onSuccess: () => setActiveModal(null) });

  const onChangeProjectStatus = (status: ProjectStatusesEnum) => {
    if (selectedProject) {
      changeProjectStatus({
        variables: {
          payload: {
            projectId: selectedProject._id,
            status,
          },
        },
      });
    }
  };

  const modalHandler = (modal: string, record: IProject) => {
    setActiveModal(modal);
    setSelectedProject(record);
  };

  const onFilterCancel = () => {
    setActiveModal(null);
    setProjectStatuses([]);
  };

  const onFilterChange = (values: any) => {
    const filter: ProjectStatusesEnum[] = [];

    Object.keys(values).forEach(key => {
      if (values[key]) {
        filter.push(key as ProjectStatusesEnum);
      }
    });

    setProjectStatuses(filter);

    if (filter.length > 0) {
      setActiveModal(null);
    }
  };

  return (
    <PageLayout pageTitle={t('projects:labels.project')}>
      {activeModal === PROJECT_MODALS.kwpProjects && (
        <KwpFetchingDataModal
          onClose={() => setActiveModal(null)}
          userLink={interfaceConnection?.link}
          activeModal={activeModal}
        />
      )}
      {activeModal === PROJECT_MODALS.deleteProject && (
        <DeleteModal
          title={t('projects:modals.deleteProject.title')}
          subTitle={t('projects:modals.deleteProject.subtitle')}
          onSubmit={() => onChangeProjectStatus(ProjectStatusesEnum.DELETED)}
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      {activeModal === PROJECT_MODALS.archiveProject && (
        <DeleteModal
          title={t('projects:modals.archiveProject.title')}
          subTitle={t('projects:modals.archiveProject.subtitle')}
          onSubmit={() => onChangeProjectStatus(ProjectStatusesEnum.ARCHIVED)}
          submitBtnText={t('projects:buttons.archive')}
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      {activeModal === PROJECT_MODALS.finishProject && (
        <DeleteModal
          title={t('projects:modals.finishProject.title')}
          subTitle={t('projects:modals.finishProject.subtitle')}
          onSubmit={() => onChangeProjectStatus(ProjectStatusesEnum.FINISHED)}
          submitBtnText={t('projects:buttons.finish')}
          offDanger
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      {activeModal === PROJECT_MODALS.unarchiveProject && (
        <DeleteModal
          title={t('projects:modals.unarchiveProject.title')}
          subTitle={t('projects:modals.unarchiveProject.subtitle')}
          onSubmit={() => onChangeProjectStatus(ProjectStatusesEnum.ACTIVE)}
          submitBtnText={t('common:buttons.unarchive')}
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      {activeModal === PROJECT_MODALS.activateProject && (
        <DeleteModal
          title={t('projects:modals.activateProject.title')}
          subTitle={t('projects:modals.activateProject.subtitle')}
          onSubmit={() => onChangeProjectStatus(ProjectStatusesEnum.ACTIVE)}
          submitBtnText={t('common:buttons.activate')}
          translate={t}
          onClose={() => setActiveModal(null)}
          isOpened
        />
      )}
      <Drawer
        visible={activeModal === PROJECT_MODALS.projectTableFilter}
        title={t('common:buttons.filterBy')}
        onClose={() => setActiveModal(null)}
      >
        <ProjectTableFilter
          onFinish={onFilterChange}
          onCancel={onFilterCancel}
          projectStatuses={projectStatuses}
        />
      </Drawer>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('projects:labels.project')}
        </Typography>
        <ButtonsWrapper>
          <SearchInput value={searchValue} onChange={e => setSearchValue(e.target.value)} />
          <Button
            type="primary"
            ghost
            icon={ICONS.filterAlt}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal(PROJECT_MODALS.projectTableFilter)}
          >
            {`${t('common:buttons.filterBy')} ${
              projectStatuses.length > 0 ? `(${projectStatuses.length})` : ''
            }`}
          </Button>
          <PermissionCheck
            action={PERMISSION_ACTIONS.create}
            permission={PLATFORM_PERMISSIONS.projectManagement}
          >
            <Button
              type="primary"
              ghost
              icon={ICONS.link}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              disabled={!isTokenValid}
              onClick={() => setActiveModal(PROJECT_MODALS.kwpProjects)}
            >
              {t('projects:buttons.syncWithKWP')}
            </Button>
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => navigate(`${ROUTES.project}/${PROJECTS_SUB_PAGES.createProject}`)}
            >
              {t('projects:buttons.createNewProject')}
            </Button>
          </PermissionCheck>
        </ButtonsWrapper>
      </HeaderContainer>
      {loading ? (
        <Loader />
      ) : projects.length > 0 ? (
        <ProjectsTable
          dataSource={projects}
          onChange={onTableChange}
          pagination={paginationConfig}
          openModal={modalHandler}
        />
      ) : (
        <EmptyContainer
          illustration={EMPTY_ILLUSTRATIONS.seo}
          emptyTitle={t('common:emptySearch.title')}
          contentText={t('common:emptySearch.subTitle')}
        />
      )}
    </PageLayout>
  );
};

export default Project;
